import { Component, OnInit, Input, ElementRef, ViewChild, OnChanges, SimpleChanges, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TicketService, TicketDTO } from 'ticket-sdk';
import { AlertController, IonInput, ModalController, Platform, ToastController } from '@ionic/angular';
import { take } from 'rxjs/operators';
import { Plugins, Capacitor } from '@capacitor/core';
import { TicketUtilityService } from '@app/core/services/ticket-utility.service';
import { KeyboardInfo } from '@capacitor/keyboard';
import { HttpErrorResponse } from '@angular/common/http';

let Keyboard;
if (Capacitor.platform !== 'web') {
  Keyboard = Plugins.Keyboard;
}

@Component({
  selector: 'app-update-license-plate-number',
  templateUrl: './update-license-plate-number.page.html',
  styleUrls: ['./update-license-plate-number.page.scss'],
})
export class UpdateLicensePlateNumberPage implements OnInit {
  @ViewChild('licensePlateNumberInput') licensePlateNumberInput: IonInput;
  
  @Input()
  ticket: TicketDTO;
  
  @Input()
  isMobile: boolean;

  keyboardHeight: number = 0;
  originalHeight: number = 32;
  licensePlateNumberForm: UntypedFormGroup;
  saving = false;

  constructor(
    public fb: UntypedFormBuilder, 
    private ticketService: TicketService,
    private modalController: ModalController,
    private changeRef: ChangeDetectorRef,
    private alertController: AlertController,
    private platform: Platform,
    private ticketUtilityService: TicketUtilityService,
    private toastController: ToastController
    ) {
    this.licensePlateNumberForm = this.fb.group({
      licensePlateNumber: ['', [
        Validators.required, 
        Validators.pattern('^([A-Za-z0-9åÅäÄöÖüÜ ])+$'),
        Validators.maxLength(10),
        Validators.minLength(2)
      ]],
    })
  }

  ngOnInit() {
    if (this.isMobile) {
      this.keyboardHeight = this.originalHeight;
    }
  }

  ionViewWillEnter() {
    if (this.platform.is('capacitor')) {
      Keyboard.addListener('keyboardWillShow', this.setKeyboardHeight);
  
      Keyboard.addListener('keyboardWillHide', this.resetKeyboardHeight);
    }
  }

  ionViewDidEnter() {
    setTimeout(() => this.licensePlateNumberInput.setFocus(), 100);
  }

  ionViewWillLeave() {
    if (this.platform.is('capacitor')) {
      Keyboard.removeAllListeners();
    }
  }

  cancel() {
    this.modalController.dismiss();
  }

  async confirmUpdate(updatedTicket: TicketDTO): Promise<boolean> {
    return new Promise(async resolve => {
      const alert = await this.alertController.create({
        cssClass: 'confirm-lpn-alert',
        header: 'Bekräfta byte av reg.nr: ',
        subHeader: updatedTicket.licensePlateNumber,
        message: 'Har du skrivit in rätt registreringsnummer?',
        buttons: [
          {
            cssClass: 'secondary',
            text: 'Nej',
            handler: () => {
              resolve(false);
            }
          }, {
            text: 'Ja',
            handler: () => {
              resolve(true);
            }
          }
        ]
      });
  
      await alert.present();
    })
  }

  licensePlateNumberChange(event) {
    let licensePlateNumber: string = this.licensePlateNumberForm.get('licensePlateNumber').value;
    if (!! licensePlateNumber && ! this.platform.is('android')) {
      this.licensePlateNumberForm.setValue({
        ...this.licensePlateNumberForm.value, 
        licensePlateNumber: licensePlateNumber.toLocaleUpperCase()
      });
    }
  }

  async onSubmit() {
    if (this.licensePlateNumberForm.valid) {
      const transformedLincensePlateNumber = this.ticketUtilityService.transformLicensePlateNumber(this.licensePlateNumberForm.value.licensePlateNumber);

      if (transformedLincensePlateNumber === this.ticket.licensePlateNumber) {
        const alert = await this.alertController.create({
          header: 'Regnummer ej bytt',
          message: 'Registreringsnumret du angav är samma som redan är kopplat till biljetten.',
          buttons: [
            {
              text: 'OK',
              role: 'cancel'
            }
          ]
        });
    
        await alert.present();
      }
      else {
        const updatedTicket: TicketDTO = {...this.ticket, licensePlateNumber: transformedLincensePlateNumber};
        this.confirmUpdate(updatedTicket).then((confirmed: boolean) => {
          if (confirmed) {
            this.saving = true;
            this.ticketService.setUserLicensePlateNumber(this.ticket.ticketId, updatedTicket)
            .pipe(take(1))
            .subscribe(
              () => this.onSuccess(updatedTicket),
              error => this.onError(error)
            )
            .add(() => this.saving = false); 
          }
          else {
            this.licensePlateNumberForm.get('licensePlateNumber').setValue('');
  
            setTimeout(() => this.licensePlateNumberInput.setFocus(), 750);
          }
        });
      }
    }
    else {
      let errorMessage: string;
      let licensePlateNumberInput = this.licensePlateNumberForm.get('licensePlateNumber');

      if (licensePlateNumberInput.errors?.pattern) {
        errorMessage = 'Registreringsnumret får inte innehålla några andra tecken än A-Ö och 0-9';
      }
      else if (licensePlateNumberInput.errors?.minlength) {
        errorMessage = 'Registreringsnumret måste innehålla minst 2 tecken';
      }
      else if (licensePlateNumberInput.errors?.maxlength) {
        errorMessage = 'Registreringsnumret får max innehålla 10 tecken';
      }
      else {
        errorMessage = 'Ogiltigt registreringsnummer';
      }
      
      this.presentErrorToast(errorMessage);
    }
  }

  async onError(error: HttpErrorResponse) {
    if (error.status === 403) {
      const alert = await this.alertController.create({
        header: 'Bytet kunde inte genomföras!',
        message: 'Du kan inte byta registreringsnummer på en biljett mer än en gång per dygn. Prova igen imorgon.',
        cssClass: 'warning-alert',
        buttons: [
          {
            text: 'Tillbaka',
            handler: () => {
              this.modalController.dismiss({success: false});
            }
          }
        ]
      });
  
      await alert.present();
    }
    else {
      this.presentErrorToast();
    }
  }

  async onSuccess(updatedTicket: TicketDTO) {
    const alert = await this.alertController.create({
      header: 'Bytet lyckades!',
      message: `Registreringsnumret har bytts från ${this.ticket.licensePlateNumber} till ${updatedTicket.licensePlateNumber}.`,
      buttons: [
        {
          text: 'Klar',
          handler: () => {
            this.modalController.dismiss({success: true});
          }
        }
      ]
    });

    await alert.present();

    alert.onDidDismiss().then(() => this.modalController.dismiss({success: true}))
  }

  async presentErrorToast(errorMessage?: string) {
    const toast = await this.toastController.create({
      message: errorMessage || 'Ett fel uppstod. Prova igen om en liten stund',
      color: 'danger',
      position: 'top',
      duration: 5000
    });
    toast.present();
  }

  resetKeyboardHeight() {
    this.keyboardHeight = this.originalHeight;
    this.changeRef.detectChanges();
  }

  setKeyboardHeight(info: KeyboardInfo) {
    this.keyboardHeight = info.keyboardHeight;
    this.changeRef.detectChanges();
  }
}
